import {Directive, ElementRef, HostListener, Injector} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator
} from '@angular/forms';

/** 整数以外が入力された場合にFormControlへ付与されるバリデーションエラーのキー */
export const INTEGER_ONLY_ERROR_KEY = 'integerOnly';

/** 入力中の表記を文字列のまま検証し、妥当な整数だけをnumber型でフォームへ渡す。 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[integerOnly]',
  providers: [
    {provide: NG_VALIDATORS, useExisting: IntegerOnlyDirective, multi: true},
    {provide: NG_VALUE_ACCESSOR, useExisting: IntegerOnlyDirective, multi: true}
  ]
})
export class IntegerOnlyDirective implements Validator, ControlValueAccessor {
  private onChange: (value: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(
    private readonly elementRef: ElementRef<HTMLInputElement>,
    private readonly injector: Injector
  ) {}

  @HostListener('input', ['$event.target.value'])
  onInput(rawValue: string): void {
    if (rawValue === '') {
      this.clearIntegerOnlyError();
      this.onChange(null);
      return;
    }
    if (this.isIntegerText(rawValue)) {
      this.clearIntegerOnlyError();
      this.onChange(Number(rawValue));
      return;
    }
    this.setIntegerOnlyError();
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  writeValue(value: number | null): void {
    this.elementRef.nativeElement.value = value === null || typeof value === 'undefined' ? '' : String(value);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const rawValue = this.elementRef.nativeElement.value;
    if (rawValue === '') {
      return null;
    }
    return this.isIntegerText(rawValue) && typeof control.value === 'number' && Number.isInteger(control.value)
      ? null
      : {[INTEGER_ONLY_ERROR_KEY]: true};
  }

  private isIntegerText(value: string): boolean {
    return /^-?\d+$/.test(value);
  }

  private setIntegerOnlyError(): void {
    const control = this.injector.get(NgControl, null)?.control;
    if (!control) { return; }
    control.setErrors({...control.errors, [INTEGER_ONLY_ERROR_KEY]: true});
  }

  private clearIntegerOnlyError(): void {
    const control = this.injector.get(NgControl, null)?.control;
    if (!control?.errors?.[INTEGER_ONLY_ERROR_KEY]) { return; }
    const errors = {...control.errors};
    delete errors[INTEGER_ONLY_ERROR_KEY];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }
}
